import { useCanvasScale } from '../use-canvas-scale'
import { useCtrlWheelZoom } from '../use-ctrl-wheel-zoom'
import { useEffectiveZoomLevel } from '../use-effective-zoom-level'
import { useFitToSpread } from '../use-fit-to-spread'
import { useFitToSpreadEffects } from '../use-fit-to-spread-effects'
import { useRulerContentOrigin } from '../use-ruler-content-origin'
import { useRulerScrollSync } from '../use-ruler-scroll-sync'
import { useSpreadLayout } from '../use-spread-layout'
import { useSpreadVisibility } from '../use-spread-visibility'
import { useViewportCoordinates } from '../use-viewport-coordinates'
import { useZoomScrollAnchor } from '../use-zoom-scroll-anchor'
import { useZoomScrollAnchorRefs } from '../use-zoom-scroll-anchor-refs'
import type { CanvasFoundation } from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Zoom and scroll: the effective canvas scale, the spread's layout in canvas points, client ↔
 * canvas coordinate conversion, ruler scroll sync, fit-to-spread, keeping the spread in view,
 * scroll anchoring across zoom changes, and Ctrl+wheel zoom.
 */
export function useCanvasViewport(
  {
    zoom,
    onZoomChange,
    onEffectiveZoomLevelChange,
    onFitToSpreadAvailabilityChange,
    pageViewMode,
    gridMode,
    bleedWidth,
    bleedHeight,
    trimWidth,
    trimHeight,
    bleedMargin,
    liveInset,
    centerGap,
    checkpointLoadKey,
  }: ResolvedCenterCanvasConfig,
  {
    scrollRef,
    pasteboardRef,
    documentCanvasRef,
  }: Pick<CanvasFoundation, 'scrollRef' | 'pasteboardRef' | 'documentCanvasRef'>
) {
  const { scale, setScale, zoomRef, scaleRef, onZoomChangeRef } = useCanvasScale({
    zoom,
    onZoomChange,
  })
  const {
    documentGridSnapStepPt,
    isSinglePage,
    pageTrimWidth,
    singlePageBleedWidth,
    fitContentDimensions,
    verticalRulerZeroAtCanvasY,
    pasteboardSize,
    snappedSpreadOriginCanvasX,
    spreadBackgroundGeometry,
    gridSnapLiveRects,
    singlePageHorizontalGuideSpan,
  } = useSpreadLayout({
    pageViewMode,
    gridMode,
    scale,
    bleedWidth,
    bleedHeight,
    trimWidth,
    trimHeight,
    bleedMargin,
  })
  const { getCanvasOrigin, computeContentPointAtClient, getCanvasPointFromPointerEvent } =
    useViewportCoordinates({ scrollRef, documentCanvasRef, scale })

  const {
    horizontalRulerStripRef,
    verticalRulerStripRef,
    rulerViewport,
    setRulerViewport,
    syncRulerScrollTransforms,
  } = useRulerScrollSync({ scrollRef })

  const {
    alignFitSpreadInViewport,
    centerSpreadForActualSize,
    recomputeFitScaleAndAlign,
    refitFitSpread,
  } = useFitToSpread({
    scrollRef,
    documentCanvasRef,
    zoomRef,
    setScale,
    fitContentDimensions,
    syncRulerScrollTransforms,
  })

  useSpreadVisibility({
    scrollRef,
    pasteboardRef,
    documentCanvasRef,
    zoomRef,
    fitContentDimensions,
    syncRulerScrollTransforms,
    alignFitSpreadInViewport,
    centerSpreadForActualSize,
  })

  useFitToSpreadEffects({
    scrollRef,
    zoom,
    setScale,
    recomputeFitScaleAndAlign,
    fitContentDimensions,
    onFitToSpreadAvailabilityChange,
  })

  const { pendingCtrlWheelAnchorRef, prevExplicitZoomAnchorRef } = useZoomScrollAnchorRefs({
    checkpointLoadKey,
  })

  useZoomScrollAnchor({
    scrollRef,
    zoom,
    scale,
    snappedSpreadOriginCanvasX,
    verticalRulerZeroAtCanvasY,
    pendingCtrlWheelAnchorRef,
    prevExplicitZoomAnchorRef,
    getCanvasOrigin,
    syncRulerScrollTransforms,
  })

  useEffectiveZoomLevel({ zoom, scale, onEffectiveZoomLevelChange })

  const { rulerContentOrigin, pasteboardScrollExtents } = useRulerContentOrigin({
    scrollRef,
    documentCanvasRef,
    pasteboardRef,
    scale,
    pasteboardSize,
    zoom,
    bleedWidth,
    bleedHeight,
    trimWidth,
    trimHeight,
    bleedMargin,
    liveInset,
    centerGap,
    checkpointLoadKey,
  })

  useCtrlWheelZoom({
    scrollRef,
    zoomRef,
    scaleRef,
    pendingCtrlWheelAnchorRef,
    onZoomChange,
    syncRulerScrollTransforms,
    computeContentPointAtClient,
  })

  return {
    scale,
    zoomRef,
    scaleRef,
    onZoomChangeRef,
    documentGridSnapStepPt,
    isSinglePage,
    pageTrimWidth,
    singlePageBleedWidth,
    fitContentDimensions,
    verticalRulerZeroAtCanvasY,
    pasteboardSize,
    snappedSpreadOriginCanvasX,
    spreadBackgroundGeometry,
    gridSnapLiveRects,
    singlePageHorizontalGuideSpan,
    getCanvasOrigin,
    computeContentPointAtClient,
    getCanvasPointFromPointerEvent,
    horizontalRulerStripRef,
    verticalRulerStripRef,
    rulerViewport,
    setRulerViewport,
    syncRulerScrollTransforms,
    centerSpreadForActualSize,
    refitFitSpread,
    pendingCtrlWheelAnchorRef,
    rulerContentOrigin,
    pasteboardScrollExtents,
  }
}
